import { Box } from '@chakra-ui/react';
import { AmplifyCore, appServiceLines, caseUtils, codeBenchActions, gradeBenchPanelActions } from '@codametrix/ui-common';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDetail, getWorkItemDetails } from '../../actions/cases.action';
import {
  CodingScreenShell,
  type ActivityFilter,
  type CodingScreenApi,
  type CodingScreenState,
  type PanelContent,
  type WorkspaceMode
} from '../../project_starburst';
import { codingScreenApiFromCase, processInstanceFromCase } from './codingScreenApi';
import { codingScreenApiFromGrading } from './gradingScreenApi';
import { NoteInteractionProvider } from './noteSlots/noteInteractionContext';
import { CODING_SCREEN_SLOT_REGISTRY } from './noteSlots/registry';
import { ActivityPanelBody } from './panels/ActivityPanelBody';
import { CaseNotesPanelBody } from './panels/CaseNotesPanelBody';

const { getCoderNotesLookupParams } = AmplifyCore.coderNotesUtils;

const RELATED_CASES_SORTABLE_PAGEABLE: CMxCommonApp.SortablePageable<any> = {
  pageableDefinition: {
    content: [],
    pageable: {
      sort: { sorted: false, unsorted: true, empty: true },
      offset: 0,
      pageSize: 25,
      pageNumber: 0,
      unpaged: false,
      paged: true
    },
    totalElements: 0,
    totalPages: 0,
    last: true,
    size: 0,
    number: 0,
    numberOfElements: 0,
    sort: { sorted: false, unsorted: true, empty: true },
    first: true,
    empty: true
  },
  sortableOptions: [
    { sortField: 'dateOfService', sortDirection: 'desc' },
    { sortField: 'caseId', sortDirection: 'desc' }
  ],
  filterableOptions: { dateFilter: undefined, filters: [] }
};

/** The react-router v5 match the route forwards — only its params are read here. */
type RouteMatch = { params?: Record<string, string | undefined> };

export type CodingScreenProps = {
  mode?: WorkspaceMode;
  /**
   * Shell state for BOTH modes, injected by `ConnectedCodingScreen`'s mapState from redux
   * (coding: the fetched case; grading: `state.codeBench.activeItem`) — see the container.
   */
  state?: CodingScreenState;
  /**
   * Route props forwarded by CMx.tsx's `render={(routeProps) => ...}`. Carries the
   * params the on-mount fetch keys on: `caseId`/`serviceLine` for coding routes,
   * `id` (= sdCaseId) for the grading route.
   */
  match?: RouteMatch;
};

/**
 * Presentational coding/grading screen. Renders the widened `CodingScreenShell` from the
 * mapState-provided `state` prop (BOTH modes). Because no ancestor fetches on the
 * project_starburst route, it dispatches its own fetch on mount to POPULATE redux, branched by `mode`:
 *
 *   coding / coding-quality → `getDetail({ caseId, serviceLine })` → `state.cases.apiResponse.activeItem`
 *   grading                 → `loadCQAItemFromURL({ params: { id } })` → `state.codeBench.activeItem`
 *
 * Both thunks populate redux but return no value used here; the shell state is read back by
 * `mapStateToCodingScreenProps`, exactly as the existing grade-bench reads `codeBench.activeItem`. So
 * this component holds NO local state.
 *
 * The fetch is guarded against double-firing (StrictMode / redux-driven re-render) by a
 * last-dispatched-key ref keyed on the route id it fetched; the effect re-fires only when the relevant
 * route params actually change.
 *
 * CODING ONLY: once `getDetail` populates `state.cases.apiResponse.activeItem` (the
 * SAME `activeCase` selector `api` is already built off), a second effect derives the primary
 * `CaseRun` (`caseUtils.getCaseRun`, the same production mapping `cases.reducer.ts` uses) and
 * dispatches `getDocumentsAsHtml` to hydrate `state.codeBench.notes` -- the redux slice
 * `mapStateToCodingScreenProps` reads back into `state.values['doc.notes']`
 * (`stateMappers/caseRunToCodingScreenState.ts`). Grading is out of scope for note display,
 * so this effect no-ops entirely under `mode === 'grading'`. Guarded the same way as the primary
 * fetch (a last-dispatched-key ref) so a redux-driven re-render doesn't re-dispatch for the same
 * case run.
 *
 * VIEWPORT-BOUNDED ROOT: the shared app shell's `.content-container`
 * (`packages/dashboard-ui/src/styles/cmx.scss`) is `height: fit-content`, so `CodingScreenTemplate`'s
 * own `h="100%"` chain -- and therefore every descendant pane's `overflow-y:auto` -- has no bounded
 * ancestor to actually clip/scroll within; the page grows to fit content instead of scrolling
 * internally. This was latent (no prior project_starburst content was tall enough to expose it)
 * until real note content -- especially `NoteFrame`'s iframe, which deliberately grows to its full
 * `scrollHeight` and disables its own scrollbar so an ANCESTOR scrolls it instead -- made it visible.
 * The `Box height="100vh" overflow="hidden"` wrapper below gives this route its own definite height
 * regardless of that ambient fit-content ancestor; bench layout's `.layout-navbar` is a
 * viewport-fixed overlay, not a flow element, so there is no separate top-chrome height to subtract.
 */
export const CodingScreen: React.FC<CodingScreenProps> = ({
  mode = 'coding',
  state,
  match
}) => {
  const dispatch = useDispatch();
  // The coding case redux already fetched by getDetail (same slice mapState reads). Build the real
  // placeHold api off it for coding; grading gets `codingScreenApiFromGrading()`, an all-stub adapter
  // (its placeHold rejects -- holds are coder-only, and grading omits the Hold slot). `codingScreenApiFromCase`
  // returns undefined until the case + its ProcessInstance are available, during which HoldAction
  // shows "Holds are not available yet."
  const activeCase = useSelector(
    (s: { cases?: { apiResponse?: { activeItem?: CMxAPI.Case } } }) =>
      s?.cases?.apiResponse?.activeItem
  );
  const sourceId = useSelector((s: { ui?: { activeUser?: { userId?: string } } }) =>
    s?.ui?.activeUser?.userId ?? ''
  );
  const serviceLineName = useSelector((s: { ui?: { serviceLine?: { name?: string } } }) =>
    s?.ui?.serviceLine?.name ?? ''
  );
  const healthSystemUid = useSelector(
    (s: { codeBench?: { userContext?: { healthSystemTenantId?: string } } }) =>
      s?.codeBench?.userContext?.healthSystemTenantId ?? ''
  );
  const tenantUid = useSelector(
    (s: { codeBench?: { userContext?: { tenantId?: string } } }) =>
      s?.codeBench?.userContext?.tenantId ?? ''
  );
  const dataAccessContext = useMemo(
    () => ({ sourceId, serviceLine: serviceLineName, healthSystemUid, tenantUid }),
    [sourceId, serviceLineName, healthSystemUid, tenantUid]
  );

  const baseApi = useMemo(
    () =>
      mode === 'grading'
        ? codingScreenApiFromGrading()
        : codingScreenApiFromCase(activeCase, dispatch, dataAccessContext),
    [mode, activeCase, dispatch, dataAccessContext]
  );

  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const api = useMemo<CodingScreenApi | undefined>(
    () =>
      baseApi && { ...baseApi, onActivityFilterChange: setActivityFilter },
    [baseApi]
  );

  const caseRun = useMemo(
    () =>
      mode !== 'grading' && activeCase?.caseRuns?.length
        ? caseUtils.getCaseRun(activeCase)
        : undefined,
    [mode, activeCase]
  );
  const processInstance = useMemo(
    () => (mode !== 'grading' ? processInstanceFromCase(activeCase) : undefined),
    [mode, activeCase]
  );
  const lookupParams = useMemo(
    () =>
      caseRun
        ? (getCoderNotesLookupParams(caseRun) as AmplifyAPI.CoderNoteInputParams)
        : undefined,
    [caseRun]
  );

  // Grading-only: the CQA activity feed keys on `sd_case_id`, which lives on the loaded CQA case
  // (`state.codeBench.activeItem`, a snake_case record — hence the narrow cast). Undefined until
  // loadCQAItemFromURL has populated it.
  const gradingSdCaseId = useSelector(
    (s: { codeBench?: { activeItem?: { sd_case_id?: string } } }) =>
      s?.codeBench?.activeItem?.sd_case_id
  );

  const lastDispatchedKey = useRef<string | null>(null);
  const params = match?.params ?? {};
  const { id, caseId, serviceLine } = params;

  useEffect(() => {
    if (mode === 'grading') {
      if (!id) {
        return;
      }
      const key = `grading:${id}`;
      if (lastDispatchedKey.current === key) {
        return;
      }
      lastDispatchedKey.current = key;
      // Populate redux (state.codeBench.activeItem); the shell state is read back by
      // mapStateToCodingScreenProps, so we do NOT unwrap the return here.
      dispatch(gradeBenchPanelActions.loadCQAItemFromURL({ params: { id } }));
      return;
    }
    if (!caseId) {
      return;
    }
    const key = `coding:${caseId}`;
    if (lastDispatchedKey.current === key) {
      return;
    }
    lastDispatchedKey.current = key;
    dispatch(getDetail({ caseId, serviceLine, processId: id }));
  }, [dispatch, mode, id, caseId, serviceLine]);

  // `case.encounter`/`workitem.encounter`'s agingDays/codableDays read off `state.cases.workItem`
  // (stateMappers/caseRunToCodingScreenState.ts), which NEITHER fetch above populates -- the legacy
  // bench fetches it separately via `loadWorkItemDetails(case_uid, caserun_uid)`
  // (code-benchv2.tsx componentDidMount/componentDidUpdate, called unconditionally regardless of
  // benchMode) into the SAME slice `code-bench-component.ts` reads for both its coding and grading
  // routes. Coding has case_uid/caserun_uid straight off this route's own match params
  // (case/:caseId/case-run/:id). Grading's route only carries the CQA `sd_case_id` as `:id` -- no
  // case_uid/caserun_uid -- so it waits for the loaded CQA record's OWN `case_uid`/`caserun_uid`
  // fields (present on the declared `CMxAPI.CaseRun` shape) once loadCQAItemFromURL resolves them.
  const gradingWorkItemKey = useSelector(
    (s: { codeBench?: { activeItem?: { case_uid?: string; caserun_uid?: string } } }) => {
      const activeItem = s?.codeBench?.activeItem;
      return activeItem?.case_uid && activeItem?.caserun_uid
        ? `${activeItem.case_uid}|${activeItem.caserun_uid}`
        : undefined;
    }
  );

  const lastWorkItemKey = useRef<string | null>(null);
  useEffect(() => {
    const key =
      mode === 'grading' ? gradingWorkItemKey : caseId && id ? `${caseId}|${id}` : undefined;
    if (!key || lastWorkItemKey.current === key) {
      return;
    }
    lastWorkItemKey.current = key;
    const [case_uid, caserun_uid] = key.split('|');
    dispatch(getWorkItemDetails({ case_uid, caserun_uid }));
  }, [dispatch, mode, caseId, id, gradingWorkItemKey]);

  // We dispatch `fetchCoderNotes` DIRECTLY (not `navigateCoderNotes`) on purpose: `navigateCoderNotes`
  // flips `state.coderNotes.isDrawerOpen`, which the globally-mounted (CMx.tsx) legacy `CMXDrawer`/
  // `CoderNotes` would react to by popping the old drawer over this screen.
  const lastCaseFetchKey = useRef<string | null>(null);
  useEffect(() => {
    if (mode === 'grading') {
      return;
    }
    if (!processInstance && !lookupParams) {
      return;
    }
    const key = `${processInstance?.guid.uuid ?? ''}|${lookupParams?.lookupValue ?? ''}`;
    if (lastCaseFetchKey.current === key) {
      return;
    }
    lastCaseFetchKey.current = key;
    if (processInstance) {
      dispatch(codeBenchActions.fetchActivity(processInstance) as any);
    }
    if (lookupParams) {
      dispatch(codeBenchActions.fetchCoderNotes(lookupParams) as any);
    }
  }, [dispatch, mode, processInstance, lookupParams]);

  // Grading Activity feed: dispatch getGraderWorkItemActivity once the loaded CQA case exposes its
  // sd_case_id (it lands after loadCQAItemFromURL). Keyed on sd_case_id so it fires once per case.
  const lastActivityKey = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== 'grading' || !gradingSdCaseId) {
      return;
    }
    if (lastActivityKey.current === gradingSdCaseId) {
      return;
    }
    lastActivityKey.current = gradingSdCaseId;
    dispatch(
      gradeBenchPanelActions.getGraderWorkItemActivity(gradingSdCaseId) as any
    );
  }, [dispatch, mode, gradingSdCaseId]);

  const panelContent = useMemo<PanelContent | undefined>(() => {
    if (mode === 'grading') {
      // Grading has no coder notes; only the Activity tab is offered, fed by the grade-bench slice.
      return {
        notes: null,
        activity: (
          <ActivityPanelBody
            mode="grading"
            sdCaseId={gradingSdCaseId}
            activityFilter={activityFilter}
          />
        )
      };
    }
    return {
      notes: lookupParams ? (
        <CaseNotesPanelBody lookupParams={lookupParams} />
      ) : null,
      activity: (
        <ActivityPanelBody
          processInstance={processInstance}
          activityFilter={activityFilter}
        />
      )
    };
  }, [mode, lookupParams, processInstance, gradingSdCaseId, activityFilter]);

  // CODING ONLY: hydrate state.codeBench.notes for the document pane. Reuses the `caseRun` memo
  // above rather than re-deriving it, and is guarded by its own last-dispatched-key ref so a
  // redux-driven re-render doesn't re-dispatch for the same case run.
  const lastNotesDispatchedKey = useRef<string | null>(null);
  useEffect(() => {
    if (mode === 'grading' || !caseRun) {
      return;
    }
    const notesKey = caseRun.caserun_uid ?? String(caseRun.case_id ?? '');
    if (!notesKey || lastNotesDispatchedKey.current === notesKey) {
      return;
    }
    lastNotesDispatchedKey.current = notesKey;
    // Populates state.codeBench.notes (GET_HTML/fulfilled); the shell state reads it back via
    // mapStateToCodingScreenProps, so we do NOT unwrap the return here.
    dispatch(codeBenchActions.getDocumentsAsHtml(caseRun));
  }, [dispatch, mode, caseRun]);

  // IPC-only: hydrate state.codeBench.relatedCases for the Encounter popover's "Concurrent cases"
  // list (coding-screen.component.ts's `siblingCaseRunsForRun` reads it back for IPC service lines).
  // Non-IPC siblings need no dispatch here -- they come from `state.cases.groupedCases`, already
  // populated as a side effect of `getDetail`'s own `getBillingGroup` call. Mirrors the legacy
  // trigger (`launch-page.action.ts`): fires once the primary case run's `encounter_identifier` and
  // service line resolve, guarded by a last-dispatched-key ref like the other fetch effects.
  // Deliberately DIFFERENT from legacy on one point: `filterSelection: {}` below sends no date-range
  // clause, whereas the legacy bench (`launch-page.action.ts`'s `getRelatedCasesInitialFilters`)
  // always scopes this same fetch to admission..discharge (or a last-30-days fallback). So this
  // popover's sibling list is not date-filtered and can include cases the legacy Related tab
  // wouldn't have shown for the same encounter. Left as-is for now -- revisit if that divergence
  // turns out to matter for this surface.
  const lastRelatedCasesKey = useRef<string | null>(null);
  useEffect(() => {
    if (mode === 'grading' || !caseRun || !AmplifyCore.isIPCServiceline(caseRun.service_line)) {
      return;
    }
    const encounterId = caseRun.encounter_identifier?.number;
    const serviceLine = appServiceLines.determineServiceLineConfig({
      name: caseRun.service_line,
      caseIdentifier: String(caseRun.case_id ?? '')
    })?.amplifyPath;
    if (!encounterId || !serviceLine) {
      return;
    }
    const key = `${encounterId}|${serviceLine}`;
    if (lastRelatedCasesKey.current === key) {
      return;
    }
    lastRelatedCasesKey.current = key;
    dispatch(
      codeBenchActions.getCasesWithEncounter({
        encounterId,
        serviceLine,
        sortablePageable: RELATED_CASES_SORTABLE_PAGEABLE,
        filterSelection: {}
      }) as any
    );
  }, [dispatch, mode, caseRun]);

  return (
    <NoteInteractionProvider>
      <Box height="100vh" overflow="hidden">
        <CodingScreenShell
          mode={mode}
          state={state}
          api={api}
          panelContent={panelContent}
          registry={CODING_SCREEN_SLOT_REGISTRY}
        />
      </Box>
    </NoteInteractionProvider>
  );
};

export { CodingScreen as default };
